/**
 * The seam between apisauce and TanStack Query.
 *
 * apisauce resolves on 4xx and 5xx rather than rejecting, so a query function that returns its
 * response lands in `success` carrying an error payload and every error state silently disappears.
 * Everything here exists to turn that back into a thrown error Query can see.
 */

export const TRANSIENT_API_PROBLEMS = ['NETWORK_ERROR', 'TIMEOUT_ERROR', 'CONNECTION_ERROR', 'SERVER_ERROR'];

/**
 * An apisauce failure as an Error, annotated with what the caller needs to branch on.
 *
 * `retryable` is decided here rather than at the retry site so that a statusless network blip is
 * still retried, while a programming error thrown inside a query function — which carries no
 * status either — is not.
 */
export const toApiError = (response = {}) => {
  const { status, problem, data } = response ?? {};
  const error = new Error(problem ?? `Request failed with status ${status ?? 'unknown'}`);

  error.status = status;
  error.problem = problem;
  error.data = data;
  error.retryable = status === undefined ? TRANSIENT_API_PROBLEMS.includes(problem) : status >= 500;

  return error;
};

/** The payload on success, a thrown {@link toApiError} otherwise. Every Linehaul query goes through here. */
export const unwrap = (response) => {
  if (response?.ok) {
    return response.data;
  }

  throw toApiError(response);
};

/** Strict on purpose: anything not marked by {@link toApiError} is treated as not worth retrying. */
export const isRetryableApiError = (error) => error?.retryable === true;
